import React from 'react';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { Tooltip, Button } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { Chart as ChartJS, BarElement, ChartOptions, ChartData } from 'chart.js';
import { LEGACY_COLORS, ICON_SIZES } from '../../../constants/graphColors';
import analyseTranslation from '../../../constants/AnalyseTranslation.json';

ChartJS.register(BarElement);

type AnalyseQualitySegmentProps = {
  segments: any;
  handleNavigation: (errorTypeId: number, segmentId?: number) => Promise<void>;
};

const options: ChartOptions<'bar'> = {
  responsive: false,
  maintainAspectRatio: false,
  scales: {
    y: {
      stacked: true,
      max: 100,
      ticks: {
        color: LEGACY_COLORS.blackOpacity,
      },
    },
    x: {
      ticks: {
        maxRotation: 0,
        autoSkip: false,
        color: LEGACY_COLORS.blackOpacity,
      },
      grid: {
        display: false,
      },
    },
  },
  layout: {
    padding: {
      top: 20,
      left: 2,
      bottom: 1,
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: false,
    },
  },
};

const AnalyseQualitySegment: React.FC<AnalyseQualitySegmentProps> = ({
  segments,
  handleNavigation,
}) => {
  const { t } = useTranslation();

  const generateSegmentData = () => {
    const progressScores = segments.map(
      (segment: { progress_score: any }) => segment.progress_score
    );

    const labels = [
      'Items',
      'Attributes',
      'Application',
      'Description',
      'Packages',
      'Pricing',
      'Extended Info',
      'Kit',
      'Interchange',
      'Digital Assets',
      'Marketing Copies',
      'Custom Fields',
    ];

    const data = {
      labels,
      datasets: [
        {
          label: 'Progress',
          data: progressScores,
          backgroundColor: Array(labels.length).fill(LEGACY_COLORS.lightGreenOpacity),
          borderColor: Array(labels.length).fill(LEGACY_COLORS.lightGreenOpacity),
          borderWidth: 2,
        },
      ],
    };
    return data;
  };

  const navigationButton = (title: string, alertTypeId: number, segmentId: number) => {
    return (
      <Button
        onClick={() => handleNavigation(alertTypeId, segmentId)}
        size="small"
        data-testid={title}
      >
        {title}
      </Button>
    );
  };

  const errorIcons = (segments: any[]) => {
    let top = ICON_SIZES.topInit;
    let left = ICON_SIZES.leftInit;

    return segments.map(segment => {
      left += ICON_SIZES.leftAdd;
      if (!segment.item_error_count) {
        return;
      }
      top = ICON_SIZES.positionIconBottom;
      top = (top * ICON_SIZES.scaleUp) / 100;
      return (
        <Tooltip
          key={segment.segment_id}
          trigger="click"
          title={navigationButton(t('homeContainer:showErrors'), 3, segment.segment_id)}
          color="white"
        >
          <span className="analyse-icons__error" style={{ left, top }}>
            <WarningOutlined style={{ fontSize: '9px', verticalAlign: 'initial' }} />
            {segment.item_error_count}
          </span>
        </Tooltip>
      );
    });
  };

  const warningIcons = (segments: any[]) => {
    const itemErrorCount = segments.map(segment => segment.item_error_count);

    let top = ICON_SIZES.topInit;
    let left = ICON_SIZES.leftInit;

    return segments.map((segment, i) => {
      left += ICON_SIZES.leftAdd;
      if (!segment.item_warning_count) {
        return;
      }
      const errorsInSegment = itemErrorCount[i] > 0;
      top = errorsInSegment ? ICON_SIZES.positionIconMiddle : ICON_SIZES.positionIconBottom;
      top = (top * ICON_SIZES.scaleUp) / 100;
      return (
        <Tooltip
          key={segment.segment_id}
          trigger="click"
          title={navigationButton(t('homeContainer:showWarnings'), 2, segment.segment_id)}
          color="white"
        >
          <span className="analyse-icons__warning" style={{ left, top }}>
            <WarningOutlined style={{ fontSize: '9px', verticalAlign: 'initial' }} />
            {segment.item_warning_count}
          </span>
        </Tooltip>
      );
    });
  };

  const infoIcons = (segments: any[]) => {
    const itemErrorCount = segments.map(segment => segment.item_error_count);
    const itemWarningCount = segments.map(segment => segment.item_warning_count);

    let top = ICON_SIZES.topInit;
    let left = ICON_SIZES.leftInit;

    return segments.map((segment, i) => {
      left += ICON_SIZES.leftAdd;
      if (!segment.item_info_count) {
        return;
      }

      const errorsInSegment = itemErrorCount[i] > 0;
      const warningsInSegment = itemWarningCount[i] > 0;
      if (errorsInSegment && warningsInSegment) {
        top = ICON_SIZES.positionIconTop;
      } else if (errorsInSegment || warningsInSegment) {
        top = ICON_SIZES.positionIconMiddle;
      } else {
        top = ICON_SIZES.positionIconBottom;
      }

      top = (top * ICON_SIZES.scaleUp) / 100;
      return (
        <Tooltip
          key={segment.segment_id}
          trigger="click"
          title={navigationButton(t('homeContainer:showRecommendations'), 1, segment.segment_id)}
          color="white"
        >
          <span className="analyse-icons__info" style={{ left, top }}>
            <WarningOutlined style={{ fontSize: '9px', verticalAlign: 'initial' }} />
            {segment.item_info_count}
          </span>
        </Tooltip>
      );
    });
  };

  const checkIfAnalysesExist = () => {
    if (segments.length === 0) {
      return (
        <span className="analyses-not-existing">{analyseTranslation.no_analyses_existing}</span>
      );
    }
  };

  const data: ChartData<'bar'> = generateSegmentData();

  return (
    <div className="analyse-quality-segment analyse__border">
      <p className="title home-container__analyse-title">
        {analyseTranslation.quaility_segment} {checkIfAnalysesExist()}
      </p>
      <Bar data={data} options={options} width={1117} height={252} />
      {Object.keys(data).length > 0 && errorIcons(segments)}
      {Object.keys(data).length > 0 && warningIcons(segments)}
      {Object.keys(data).length > 0 && infoIcons(segments)}
    </div>
  );
};

export default AnalyseQualitySegment;
